import { useRef, useEffect } from "react";
import { drawShape, isActive } from "../lib/annotations";

// Client-facing playback: original video + timed drawings overlay + synced voiceover + written notes.
export function AnnotatedPlayer({ videoUrl, voiceoverUrl, annotations = [], text, coachName }) {
  const vRef = useRef(null);
  const cRef = useRef(null);
  const aRef = useRef(null);

  useEffect(() => {
    const v = vRef.current, c = cRef.current;
    if (!v || !c) return;
    let raf;
    const redraw = () => {
      const w = (c.width = c.clientWidth), h = (c.height = c.clientHeight);
      const ctx = c.getContext("2d");
      ctx.clearRect(0, 0, w, h);
      annotations.filter((s) => isActive(s, v.currentTime)).forEach((s) => drawShape(ctx, s, w, h));
      raf = requestAnimationFrame(redraw);
    };
    raf = requestAnimationFrame(redraw);
    return () => cancelAnimationFrame(raf);
  }, [annotations]);

  useEffect(() => {
    const v = vRef.current, a = aRef.current;
    if (!v || !a || !voiceoverUrl) return;
    const onPlay = () => { a.currentTime = v.currentTime; a.play().catch(() => {}); };
    const onPause = () => a.pause();
    const onSeek = () => { a.currentTime = v.currentTime; };
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    v.addEventListener("seeked", onSeek);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("seeked", onSeek);
    };
  }, [voiceoverUrl]);

  return (
    <div data-testid="coach-response-player">
      <div className="relative bg-black aspect-video border border-[#C9A227]/40 overflow-hidden">
        <video ref={vRef} src={videoUrl} controls playsInline className="w-full h-full object-contain" />
        <canvas ref={cRef} className="absolute inset-0 w-full h-full pointer-events-none" />
        {voiceoverUrl && (
          <div className="absolute top-2 right-2 overline px-2 py-1 bg-[#C9A227] text-[#050A08] font-bold">♪ Voiceover</div>
        )}
      </div>
      {voiceoverUrl && <audio ref={aRef} src={voiceoverUrl} preload="auto" />}
      <div className="mt-5 border-l-2 border-[#C9A227] pl-5">
        <div className="overline" style={{ color: "#C9A227" }}>Coach feedback{coachName ? ` · ${coachName}` : ""}</div>
        {text ? (
          <p className="text-white/85 mt-2 whitespace-pre-wrap" data-testid="coach-response-text">{text}</p>
        ) : (
          <p className="text-white/50 mt-2">Press play to see the annotations{voiceoverUrl ? " and hear the voiceover" : ""}.</p>
        )}
        {voiceoverUrl && <p className="text-white/40 text-xs mt-3">Tip: the voiceover plays with the video — press play above.</p>}
      </div>
    </div>
  );
}
